import { Page } from "pages/Page";
import { RouteLink, RouteList } from "Routes";
import { PageHeader } from "components/PageHeader";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "contexts/GlobalContext";
import { GetPresetsTopicResult } from "results/frankenstories/presets/GetPresetsTopicResult";
import { ActivePrompt } from "components/prompts/ActivePrompt";
import { PresetPromptDto } from "dtos/frankenstories/prompts/PresetPromptDto";
import { PromptSearch, PromptTag } from "components/prompts/PromptSearch";
import { PromptsDisplay } from "components/prompts/PromptsDisplay";
import { useScrollToTop } from "hooks/useScrollToTop";
import { PromptCollectionDisplay } from "components/prompts/PromptCollectionDisplay";
import { IdNameDto } from "dtos/IdNameDto";
import { TabsPlaceholder } from "components/placeholders/TabsPlaceholder";
import { TabButton } from "components/TabButton";
import { IconText } from "components/IconText";
import { Link } from "react-router-dom";

const DiscoverTopicPage = (props: { friendlyUrl: string; prompt?: string }) => {
    const { friendlyUrl, prompt } = props;

    const { presetsService, catchServerError, getPromptSearchTags } = useContext(GlobalContext);
    const { getPresetsTopic } = presetsService;

    const [data, setData] = useState<GetPresetsTopicResult>();
    const [activePrompt, setActivePrompt] = useState<PresetPromptDto>();
    const [promptTags, setPromptTags] = useState<Array<PromptTag>>();
    const [pageTags, setPageTags] = useState<Array<IdNameDto>>([]);
    const [filterTagId, setFilterTagId] = useState<number>();
    const [prompts, setPrompts] = useState<Array<PresetPromptDto>>();

    useScrollToTop();

    useEffect(() => {
        let mounted = true;

        if (friendlyUrl) {
            getPresetsTopic(friendlyUrl)
                .then(x => {
                    if (mounted) {
                        setData(x);
                    }
                })
                .catch(catchServerError);
        }

        return () => {
            mounted = false;
        };
    }, [catchServerError, friendlyUrl, getPresetsTopic]);

    // prompt tags
    useEffect(() => {
        let mounted = true;

        getPromptSearchTags()
            .then(x => {
                if (mounted) {
                    setPromptTags(x);
                }
            })
            .catch(catchServerError);

        return () => {
            mounted = false;
        };
    }, [catchServerError, getPromptSearchTags]);

    // filter tags
    useEffect(() => {
        if (promptTags && data) {
            const pageTagIds = new Set(data.prompts.flatMap(x => x.tagIds));

            const newTags = promptTags
                .filter(x => x.tagType === "tag" && pageTagIds.has(x.tagId))
                .map(x => ({ id: x.tagId, name: x.name }));

            setPageTags(newTags);
        }
    }, [promptTags, data]);

    // filter prompts
    useEffect(() => {
        if (data) {
            let newData = [...data.prompts];

            // filter by tag
            if (filterTagId) {
                newData = newData.filter(x => x.tagIds.includes(filterTagId));
            }

            // sort by name
            newData.sort((a, b) => a.name.localeCompare(b.name));

            setPrompts(newData);
        }
    }, [data, filterTagId]);

    // clear filter on page change
    useEffect(() => {
        setFilterTagId(undefined);
    }, [friendlyUrl]);

    // select prompt on page load
    useEffect(() => {
        if (prompt && prompts) {
            // find prompt
            const foundPrompt = prompts.find(x => x.friendlyUrl === prompt);

            if (foundPrompt) {
                setActivePrompt(foundPrompt);
            }
        }
    }, [prompt, prompts]);

    const title = data?.name ?? "...";

    return (
        <Page
            title={title}
            description={data?.description ?? ""}
            canonical={RouteLink.promptsTopic(friendlyUrl, prompt)}
            authType="Public"
        >
            <div className="grid gap-4 gap-lg-5">
                <div className="g-col-12">
                    <PageHeader
                        title={data?.name}
                        description={data?.description}
                        icon="🧭"
                        action={
                            <Link to={RouteList.Discover} className="btn btn-dark icon-link">
                                <IconText icon="fas fa-arrow-left" text="Back to Discover" />
                            </Link>
                        }
                    />
                </div>

                <div className="g-col-12 g-col-lg-8">
                    <div className="vstack gap-4">
                        <PromptSearch />

                        {pageTags.length > 0 ? (
                            <div className="tags-list">
                                <TabButton
                                    isSelected={!filterTagId}
                                    text="All tags"
                                    size="sm"
                                    onClick={() => setFilterTagId(undefined)}
                                />

                                {pageTags.map(t => (
                                    <TabButton
                                        key={t.id}
                                        isSelected={filterTagId === t.id}
                                        text={t.name}
                                        size="sm"
                                        onClick={() => setFilterTagId(t.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <TabsPlaceholder />
                        )}

                        {data?.additionalInfo && (
                            <div
                                className="text-center text-lg-start border p-3 auto-white-link"
                                dangerouslySetInnerHTML={{ __html: data.additionalInfo }}
                            ></div>
                        )}

                        <PromptsDisplay
                            prompts={prompts}
                            setActivePrompt={setActivePrompt}
                            showLoading={true}
                            activePrompt={activePrompt}
                        />
                    </div>
                </div>

                <div className="g-col-12 g-col-lg-4">
                    <PromptCollectionDisplay collections={data?.collections} />
                </div>
            </div>

            <ActivePrompt prompt={activePrompt} setActivePrompt={setActivePrompt} />
        </Page>
    );
};

export default DiscoverTopicPage;
